"use client"

import type { EventDetails } from "@/modules/events/types/eventDetailsApi"
import type { I18nLocale } from "@/modules/i18n/types"
import type { FunctionComponent } from "react"

import clsx from "clsx"
import { format, isSameDay } from "date-fns"
import Skeleton from "react-loading-skeleton"

import { Calendar } from "@jobteaser/spark/components/Icons/Calendar"
import { Text } from "@jobteaser/spark/components/Text"

import {
  getCurrentLocaleDate,
  getFormattedTimeZoneOffset,
  getLightDateFormatFromLocale,
  getTimeFormatFromLocale,
} from "@/modules/dates/dateFns"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./MainDetails.module.css"

type EventDatesProps = {
  locale: I18nLocale
  endDate: EventDetails["endDate"]
  startDate: EventDetails["startDate"]
}

export const EventDates: FunctionComponent<EventDatesProps> = ({
  endDate: rawEndDate,
  locale: currentLocale,
  startDate: rawStartDate,
}) => {
  const { t, ready } = useTranslation(["shared_date", "shared_time"], true)

  if (!ready) {
    return <Skeleton containerClassName={clsx(styles.flexContainer, styles.eventDatesPlaceholder)} height="48px" />
  }

  const dateLocale = getCurrentLocaleDate(currentLocale)
  const dateFormat = getLightDateFormatFromLocale(currentLocale, false)
  const timeFormat = getTimeFormatFromLocale(currentLocale)

  const timeZone = getFormattedTimeZoneOffset(new Date(rawStartDate))
  const startDate = format(new Date(rawStartDate), dateFormat, { locale: dateLocale })
  const startTime = `${format(new Date(rawStartDate), timeFormat)}${currentLocale === "de" ? " Uhr" : ""}`
  const endDate = format(new Date(rawEndDate), dateFormat, { locale: dateLocale })
  const endTime = `${format(new Date(rawEndDate), timeFormat)}${currentLocale === "de" ? " Uhr" : ""}`
  const isSeveralDaysLong = !isSameDay(new Date(rawStartDate), new Date(rawEndDate))

  return (
    <div className={styles.flexContainer} data-testid="event-details-main-details-event-dates">
      <Calendar className={styles.icon} data-testid="event-details-main-details-event-icon" />
      <time dateTime={`${rawStartDate}`} role="time">
        {isSeveralDaysLong ? (
          <>
            {t("shared_date.from")}
            <Text
              as="span"
              className={styles.detailsDate}
              data-testid="event-details-main-details-start-date"
              variant="body1"
              weight="bold"
            >
              {startDate}
            </Text>
            <Text as="span" data-testid="event-details-main-details-start-time" variant="body1">
              {startTime}
            </Text>
            <br />
            {t("shared_date.to")}
            <Text
              as="span"
              className={styles.detailsDate}
              data-testid="event-details-main-details-end-date"
              variant="body1"
              weight="bold"
            >
              {endDate}
            </Text>
            <Text
              as="span"
              data-testid="event-details-main-details-end-time"
              variant="body1"
            >{`${endTime} ${timeZone}`}</Text>
          </>
        ) : (
          <>
            <Text as="span" data-testid="event-details-main-details-start-date" variant="body1" weight="bold">
              {startDate}
            </Text>
            <br />
            <Text as="span" data-testid="event-details-main-details-event-time-from-to" variant="body1">
              {`${t("shared_time.from_to", { endTime, startTime })} ${timeZone}`}
            </Text>
          </>
        )}
      </time>
    </div>
  )
}
